import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { FieldWorker, QuickDeliveryHandlingPolicy, QuickDeliveryHandlingResult } from './field-work.models';
import { FieldWorkService } from './field-work.service';

@Component({
  selector: 'app-quick-delivery-handling-dialog',
  standalone: true,
  imports: [
    CommonModule, FormsModule, ButtonModule, CheckboxModule, DatePickerModule, DialogModule,
    IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule,
    MultiSelectModule, TagModule, TextareaModule
  ],
  templateUrl: './quick-delivery-handling-dialog.component.html',
  styleUrls: ['./quick-delivery-handling-dialog.component.css']
})
export class QuickDeliveryHandlingDialogComponent implements OnChanges {
  private readonly service = inject(FieldWorkService);

  @Input() visible = false;
  @Input() deliveryId?: number;
  @Input() deliveryReference?: string;
  @Input() defaultDriverId?: number;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter<QuickDeliveryHandlingResult>();
  @Output() controlledFieldWorkRequested = new EventEmitter<void>();

  workers: FieldWorker[] = [];
  policy: QuickDeliveryHandlingPolicy = {};
  selectedWorkerIds: number[] = [];
  loadingSelected = true;
  unloadingSelected = false;
  actualHours = 1;
  workDate = new Date();
  notes = '';
  evidenceUrl = '';
  evidenceFileName = '';
  loading = false;
  saving = false;
  uploading = false;
  error = '';
  result?: QuickDeliveryHandlingResult;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) this.open();
  }

  get activityCount(): number {
    return Number(this.loadingSelected) + Number(this.unloadingSelected);
  }

  get selectedActivities(): Array<'LOADING' | 'UNLOADING'> {
    return [this.loadingSelected ? 'LOADING' : null, this.unloadingSelected ? 'UNLOADING' : null]
      .filter((value): value is 'LOADING' | 'UNLOADING' => Boolean(value));
  }

  get workerOptions(): Array<FieldWorker & { label: string }> {
    return this.workers.map(worker => ({
      ...worker,
      label: [worker.displayName || worker.workerNumber || 'Field Person', worker.workerCategory]
        .filter(Boolean).join(' · ')
    }));
  }

  get evidenceRequired(): boolean {
    return Boolean(this.policy.evidenceRequired);
  }

  get defaultDriverWorker(): FieldWorker | undefined {
    return this.workers.find(worker => worker.driverId === this.defaultDriverId);
  }

  get busy(): boolean {
    return this.loading || this.saving || this.uploading;
  }

  open(): void {
    this.error = '';
    this.result = undefined;
    this.notes = '';
    this.evidenceUrl = '';
    this.evidenceFileName = '';
    this.workDate = new Date();
    this.actualHours = 1;
    this.loadingSelected = this.policy.loadingEnabled !== false;
    this.unloadingSelected = false;
    this.loadOptions();
  }

  close(): void {
    if (!this.busy) this.visibleChange.emit(false);
  }

  reset(): void {
    if (this.busy) return;
    this.open();
  }

  complete(): void {
    if (!this.deliveryId || this.busy) return;
    this.error = '';
    this.result = undefined;
    if (!this.selectedWorkerIds.length) {
      this.error = 'Select at least one worker.';
      return;
    }
    if (!this.selectedActivities.length) {
      this.error = 'Select Loading, Unloading, or both.';
      return;
    }
    if (!this.actualHours || this.actualHours <= 0) {
      this.error = 'Actual hours must be greater than zero.';
      return;
    }
    if (this.evidenceRequired && !this.evidenceUrl) {
      this.error = 'Evidence is required by the delivery-handling policy.';
      return;
    }
    this.saving = true;
    this.service.completeQuickDeliveryHandling(this.deliveryId, {
      fieldWorkerIds: this.selectedWorkerIds,
      activities: this.selectedActivities,
      workDate: this.workDate,
      actualHours: this.actualHours,
      evidenceUrl: this.evidenceUrl || undefined,
      notes: this.notes.trim() || undefined
    }).pipe(finalize(() => this.saving = false)).subscribe({
      next: result => {
        this.result = result;
        this.completed.emit(result);
      },
      error: error => this.error = this.errorMessage(error)
    });
  }

  selectEvidence(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      this.error = 'Evidence files cannot exceed 10 MB.';
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => this.error = 'The selected evidence file could not be read.';
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const base64Content = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
      if (!base64Content) {
        this.error = 'The selected evidence file could not be read.';
        return;
      }
      this.uploading = true;
      this.error = '';
      this.service.uploadEvidence({ fileName: file.name, mimeType: file.type || 'application/octet-stream', base64Content })
        .pipe(finalize(() => this.uploading = false)).subscribe({
          next: upload => {
            this.evidenceUrl = upload.fileUrl || '';
            this.evidenceFileName = upload.fileName || file.name;
          },
          error: error => this.error = this.errorMessage(error)
        });
    };
    reader.readAsDataURL(file);
  }

  clearEvidence(): void {
    this.evidenceUrl = '';
    this.evidenceFileName = '';
  }

  useDeliveryDriver(): void {
    const worker = this.defaultDriverWorker;
    if (worker?.fieldWorkerId) this.selectedWorkerIds = [worker.fieldWorkerId];
  }

  useControlledFieldWork(): void {
    if (!this.busy) this.controlledFieldWorkRequested.emit();
  }

  private loadOptions(): void {
    this.loading = true;
    this.service.quickDeliveryHandlingPolicy().subscribe({
      next: policy => {
        this.policy = policy;
        this.loadingSelected = policy.loadingEnabled !== false;
        if (policy.unloadingEnabled === false) this.unloadingSelected = false;
      },
      error: error => this.error = this.errorMessage(error)
    });
    this.service.people({ page: 0, pageSize: 250 }).pipe(finalize(() => this.loading = false)).subscribe({
      next: page => {
        this.workers = (page.content || []).filter(worker => worker.status === 'ACTIVE');
        const driver = this.defaultDriverWorker;
        this.selectedWorkerIds = driver?.fieldWorkerId ? [driver.fieldWorkerId] : [];
      },
      error: error => this.error = this.errorMessage(error)
    });
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message || 'Could not complete quick handling.';
  }
}
